import type {
  AddMailingListRecipientPayload,
  MailingListRecipient,
  UpdateMailingListRecipientPayload,
} from '@gozoki/api-types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { gozokiApiWithAuth } from '../gozokiApi'
import { MAILING_LISTS_QUERY_KEY, mailingListDetailQueryKey } from '../queries/mailingLists.queries'

// Add a recipient to a mailing list
export const addMailingListRecipient = async (
  mailingListKey: string,
  payload: AddMailingListRecipientPayload,
): Promise<MailingListRecipient> => {
  const response = await gozokiApiWithAuth.post<MailingListRecipient>(
    `/mailing-lists/${mailingListKey}/recipients`,
    payload,
  )
  return response.data
}

// Update a mailing list recipient
export const updateMailingListRecipient = async ({
  id,
  payload,
}: {
  id: number
  payload: UpdateMailingListRecipientPayload
}): Promise<MailingListRecipient> => {
  const response = await gozokiApiWithAuth.patch<MailingListRecipient>(
    `/mailing-lists/recipients/${id}`,
    payload,
  )
  return response.data
}

// Destroy a mailing list recipient
export const destroyMailingListRecipient = async (id: number): Promise<void> => {
  await gozokiApiWithAuth.delete(`/mailing-lists/recipients/${id}`)
}

export const useAddMailingListRecipient = (mailingListKey: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: AddMailingListRecipientPayload) =>
      addMailingListRecipient(mailingListKey, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MAILING_LISTS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: mailingListDetailQueryKey(mailingListKey) })
    },
  })
}

export const useUpdateMailingListRecipient = (mailingListKey: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateMailingListRecipient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MAILING_LISTS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: mailingListDetailQueryKey(mailingListKey) })
    },
  })
}

export const useDestroyMailingListRecipient = (mailingListKey: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: destroyMailingListRecipient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MAILING_LISTS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: mailingListDetailQueryKey(mailingListKey) })
    },
  })
}
